import { Link } from 'react-router-dom';

/**
 * Editorial section heading — BUILD-PLAN §5, §11.
 *
 * The eyebrow/heading/link lockup repeats across every homepage section, so
 * it lives here rather than being re-typed with slightly different spacing
 * each time (§44 "do not duplicate components unnecessarily").
 */

interface SectionHeadingProps {
  eyebrow?: string;
  title: string;
  description?: string;
  link?: { to: string; label: string };
  /**
   * Editorial index, e.g. `1` renders as "01".
   *
   * Numbered sections are one of the cheapest signals of an edited page: two
   * digits and a rule tell the reader the sequence was composed rather than
   * accumulated. Optional, because a section that is not part of the numbered
   * run should not pretend to be.
   */
  index?: number;
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  link,
  index,
}: SectionHeadingProps) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 border-t border-line pt-6">
      <div className="max-w-xl">
        {/* Rule, index and eyebrow on one line — the same lockup the hero
            opens with, so the page reads as one system rather than a set of
            sections that happen to share a font. */}
        {(eyebrow || index !== undefined) && (
          <div className="flex items-center gap-3">
            {index !== undefined && (
              <span aria-hidden="true" className="font-display text-sm text-accent/70">
                {String(index).padStart(2, '0')}
              </span>
            )}
            <span className="h-px w-8 bg-accent/50" aria-hidden="true" />
            {eyebrow && <p className="eyebrow !mt-0">{eyebrow}</p>}
          </div>
        )}

        <h2 className="mt-4 font-display text-[1.75rem] leading-[1.15] text-ink sm:text-3xl lg:text-4xl">
          {title}
        </h2>

        {description && (
          <p className="mt-3 max-w-md text-sm leading-relaxed text-muted">{description}</p>
        )}
      </div>

      {link && (
        <Link
          to={link.to}
          className="group inline-flex items-center gap-2 border-b border-accent pb-1 text-sm tracking-wide text-ink transition-colors hover:text-accent"
        >
          {link.label}
          <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
            →
          </span>
        </Link>
      )}
    </div>
  );
}
